import { DisplayMarket, makeUsd, MarketMeta } from "@silo/types";
import { Array, BigInt, Effect, Option, pipe } from "effect";
import { getFallbackDisplayMarketAsset } from "./get-fallback-display-market-asset";
import { getHasUnstableOracle, getWarningConfig } from "@silo/config";

export type GetFallbackDisplayMarket = ReturnType<
  typeof getFallbackDisplayMarket
>;
export type GetFallbackDisplayMarketSuccess =
  Effect.Effect.Success<GetFallbackDisplayMarket>;
export type GetFallbackDisplayMarketError =
  Effect.Effect.Error<GetFallbackDisplayMarket>;
export type GetFallbackDisplayMarketContext =
  Effect.Effect.Context<GetFallbackDisplayMarket>;

export function getFallbackDisplayMarket(marketMeta: MarketMeta) {
  return Effect.gen(function* () {
    const { chainKey, protocolKey, marketAddress, isDeprecated } = marketMeta;

    const [baseAsset, firstBridgeAsset, secondBridgeAsset] = yield* Effect.all(
      [
        getFallbackDisplayMarketAsset(marketMeta.baseAsset),
        getFallbackDisplayMarketAsset(marketMeta.bridgeAssets[0]),
        pipe(
          marketMeta.bridgeAssets[1],
          Option.fromNullable,
          Option.match({
            onNone: () => Effect.succeed(undefined),
            onSome: (marketAssetMeta) =>
              getFallbackDisplayMarketAsset(marketAssetMeta),
          })
        ),
      ],
      { concurrency: "unbounded" }
    );

    const warningConfig = getWarningConfig({ protocolKey, marketAddress });

    const hasUnstableOracle = getHasUnstableOracle({
      protocolKey,
      marketAddress,
    });

    const assets = pipe(
      [baseAsset, firstBridgeAsset, secondBridgeAsset],
      Array.flatMapNullable((asset) => asset)
    );

    const liquidityUsd = pipe(
      assets,
      Array.map((asset) => asset.liquidityUsd),
      BigInt.sumAll,
      makeUsd
    );

    const tvlUsd = pipe(
      assets,
      Array.map((asset) => asset.tvlUsd),
      BigInt.sumAll,
      makeUsd
    );

    const displayMarket: DisplayMarket = {
      chainKey,
      protocolKey,
      marketAddress,
      marketName: baseAsset.name,
      marketSymbol: baseAsset.symbol,
      marketTokenAddress: baseAsset.tokenAddress,
      warningConfig,
      hasUnstableOracle,
      isDeprecated,
      liquidityUsd,
      tvlUsd,

      baseAsset,
      bridgeAssets: secondBridgeAsset
        ? [firstBridgeAsset, secondBridgeAsset]
        : [firstBridgeAsset],
    };

    return displayMarket;
  });
}
